/**
 * /weather/report/:id — full detail for a single community weather report.
 * Photos gallery, metrics, verify/dispute action, mini locality info.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import EntityMessageBoard from "@/components/EntityMessageBoard";
import ReportRichText from "@/components/ReportRichText";
import {
  ArrowLeft, Loader2, MapPin, ShieldCheck, ThumbsUp, ThumbsDown, Sun, CloudRain,
  Snowflake, Wind, Cloud, CloudLightning, Camera, Droplets, ExternalLink, StickyNote,
} from "lucide-react";

const REPORT_ICON = {
  heavy_rain: CloudRain, flooding: CloudRain, flash_flood: CloudRain,
  thunderstorm: CloudLightning, lightning: CloudLightning,
  snow: Snowflake, heavy_snow: Snowflake, blizzard: Snowflake, ice_storm: Snowflake,
  hail: Snowflake, wildfire: Sun, smoke: Sun, extreme_heat: Sun, heat_wave: Sun,
  tornado: Wind, hurricane: Wind, cyclone: Wind, high_wind: Wind, dust_storm: Wind,
  fog: Cloud, downed_tree: Wind, landslide: CloudRain, extreme_cold: Snowflake,
};
const SEVERITY_COLOR = { mild: "#22c55e", moderate: "#f59e0b", severe: "#f97316", critical: "#dc2626" };
const _titleize = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function WeatherReportDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [report,  setReport]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState(null);

  const load = async () => {
    try {
      const { data } = await http.get(`/weather-reports/${id}`);
      setReport(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Report not found");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const verify = async (kind) => {
    if (!user) { toast.error("Sign in to verify reports"); return; }
    setBusy(true);
    try {
      const { data } = await http.post(`/weather-reports/${id}/verify`, { kind });
      setReport(data);
      toast.success(kind === "confirm" ? "Confirmed" : kind === "dispute" ? "Disputed" : "Resolved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit verification");
    } finally { setBusy(false); }
  };

  if (loading) return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="wofy-card !p-6 inline-flex items-center gap-2" data-testid="wr-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading report…
      </div>
    </div>
  );
  if (error) return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="wofy-card !p-6" style={{ background: "#FEF3C7" }}>{error}</div>
    </div>
  );
  if (!report) return null;

  const Icon = REPORT_ICON[report.type] || Cloud;
  const sev = SEVERITY_COLOR[report.severity] || "#6b7280";
  const observedAt = report.created_at ? new Date(report.created_at).toLocaleString() : null;
  const verifyCount = (report.verifications || []).length;
  const disputeCount = (report.disputes || []).length;
  const isOwner = user && user.id === report.user_id;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="weather-report-detail">
      <Link to="/weather/community" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-3">
        <ArrowLeft className="w-3 h-3" /> All community reports
      </Link>

      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
             style={{ background: `${sev}18` }}>
          <Icon className="w-8 h-8" style={{ color: sev }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
                  style={{ background: `${sev}22`, color: sev }}>
              {_titleize(report.severity || "moderate")}
            </span>
            {report.verified && (
              <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1"
                    style={{ background: "#16a34a22", color: "#16a34a" }}
                    data-testid="wr-verified-badge">
                <ShieldCheck className="w-3 h-3" /> Verified
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">{_titleize(report.type)}</h1>
          <div className="mt-1 text-sm opacity-80 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {[report.city, report.region, report.country].filter(Boolean).join(", ") || "Somewhere"}
          </div>
          <div className="mt-0.5 text-xs opacity-60">
            {report.author_name || "Anonymous"} · {observedAt}
          </div>
        </div>
      </div>

      {/* Photos */}
      {report.photo_urls?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="wr-photos">
          {report.photo_urls.map((u, i) => (
            <img key={i} src={u} alt={`report-photo-${i}`}
                 className="rounded-2xl border max-h-96 w-full object-cover"
                 style={{ borderColor: "var(--wofy-border)" }} />
          ))}
        </div>
      )}

      {/* Description */}
      {report.description && (
        <div className="wofy-card !p-6 mb-4" data-testid="wr-description">
          <ReportRichText text={report.description} className="opacity-90" />
        </div>
      )}

      {/* Notes (long-form) */}
      {report.notes && (
        <div className="wofy-card !p-6 mb-4" data-testid="wr-notes">
          <div className="overline mb-2 inline-flex items-center gap-1" style={{ color: "var(--wofy-primary)" }}>
            <StickyNote className="w-3 h-3" /> NOTES
          </div>
          <ReportRichText text={report.notes} className="opacity-90 text-sm" />
        </div>
      )}

      {/* Videos */}
      {report.videos?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="wr-videos">
          {report.videos.map((v, i) => (
            <VideoEmbed key={i} video={v} />
          ))}
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="wr-metrics">
        {report.rainfall_mm != null && <Metric label="Rainfall" value={`${report.rainfall_mm} mm`} icon={Droplets} color="#0284c7" />}
        {report.snow_cm     != null && <Metric label="Snow depth" value={`${report.snow_cm} cm`}    icon={Snowflake} color="#0ea5e9" />}
        {report.hail_size_mm!= null && <Metric label="Hail size" value={`${report.hail_size_mm} mm`} icon={Snowflake} color="#94a3b8" />}
        {report.wind_kph    != null && <Metric label="Peak wind" value={`${report.wind_kph} km/h`} icon={Wind}      color="#7c3aed" />}
      </div>

      {/* Verification */}
      <div className="wofy-card !p-6 mb-6" data-testid="wr-verify-panel">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>NEIGHBOUR VERIFICATION</div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="text-sm">
            <span className="font-medium">{verifyCount}</span> confirm ·{" "}
            <span className="font-medium">{disputeCount}</span> dispute
          </div>
          <div className="ml-auto flex gap-2">
            <button type="button" disabled={busy || isOwner} onClick={() => verify("confirm")}
                    className="wofy-btn-primary text-sm"
                    data-testid="wr-confirm-btn">
              <ThumbsUp className="w-4 h-4" /> Confirm
            </button>
            <button type="button" disabled={busy || isOwner} onClick={() => verify("dispute")}
                    className="wofy-btn-ghost text-sm"
                    data-testid="wr-dispute-btn">
              <ThumbsDown className="w-4 h-4" /> Dispute
            </button>
          </div>
        </div>
        <div className="mt-2 text-xs opacity-70">
          You must be within 50 km of the report location to verify it. Three confirmations promote a report to <em>verified</em>.
        </div>
      </div>

      {/* Coordinates + link to map */}
      <div className="text-xs opacity-60 font-mono mb-6">
        {report.lat?.toFixed(4)}, {report.lng?.toFixed(4)}
      </div>

      <Link to="/weather?report=1" className="wofy-btn-primary" data-testid="wr-add-your-own">
        <Camera className="w-4 h-4" /> 🌦️ Report Local Weather
      </Link>

      {/* Discussion forum — Facebook-style thread scoped to this report */}
      <div className="mt-10">
        <EntityMessageBoard
          entityType="weather_report"
          entityId={report.id}
          title="Neighbourhood discussion"
        />
      </div>
    </div>
  );
}

function VideoEmbed({ video }) {
  if (!video?.embed_url) {
    // Instagram / unsupported → render a click-through preview card.
    return (
      <a
        href={video?.watch_url || "#"}
        target="_blank"
        rel="noopener noreferrer"
        className="wofy-card !p-6 flex items-center gap-3 hover:shadow-md transition-shadow"
        data-testid="wr-video-link"
      >
        <div
          className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 text-white font-bold"
          style={{ background: "linear-gradient(135deg,#833AB4,#FD1D1D,#FCB045)" }}
        >
          IG
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Instagram {video?.kind === "reel" || video?.kind === "reels" ? "reel" : "post"}</div>
          <div className="text-xs opacity-60 truncate">{video?.watch_url}</div>
        </div>
        <ExternalLink className="w-4 h-4 opacity-50" />
      </a>
    );
  }
  return (
    <div className="rounded-2xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
      <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
        <iframe
          title={`video-${video.provider}`}
          src={video.embed_url}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          data-testid={`wr-video-embed-${video.provider}`}
        />
      </div>
    </div>
  );
}

function Metric({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-2xl border p-3" style={{ borderColor: "var(--wofy-border)" }}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center"
           style={{ background: `${color}18`, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-2 font-display text-lg font-bold" style={{ color }}>{value}</div>
      <div className="text-[11px] uppercase font-mono tracking-wider opacity-70">{label}</div>
    </div>
  );
}
